import { createClient } from '@supabase/supabase-js';
import type { DataSource } from '../contexts/DataSourceContext';

// Try both import.meta.env and process.env (for Vite config define)
const rawUrl = import.meta.env?.VITE_SUPABASE_URL || (process.env as any)?.VITE_SUPABASE_URL || '';
// remove trailing slashes just in case
const url = rawUrl.replace(/\/+$/, '');

const anon = import.meta.env?.VITE_SUPABASE_ANON_KEY || import.meta.env?.VITE_SUPABASE_KEY ||
            (process.env as any)?.VITE_SUPABASE_ANON_KEY || (process.env as any)?.VITE_SUPABASE_KEY || '';

if (!url || !anon) {
  throw new Error('❌ Missing Supabase env vars. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_KEY) to .env.local');
}

export const supabase = createClient(url, anon, {
  auth: {
    persistSession: true,
    // unique key -> removes "Multiple GoTrueClient instances" race
    storageKey: 'lenders-dev-auth',
  },
});

// Enhanced query functions with error handling and type safety
export const supabaseService = {
  /**
   * Get the table name based on the data source version
   */
  getFinancialStatsTable(dataSource: DataSource = 'v2') {
    return dataSource === 'v1' ? 'financial_health_stats' : 'financial_health_stats_v2';
  },

  /**
   * Fetch members with optional filtering
   */
  async getMembers(options?: {
    limit?: number;
    offset?: number;
    filters?: Record<string, any>;
    orderBy?: { column: string; ascending?: boolean };
  }) {
    try {
      let query = supabase.from('members').select('*');

      // Apply filters if provided
      if (options?.filters) {
        Object.entries(options.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            query = query.eq(key, value);
          }
        });
      }

      // Apply ordering
      if (options?.orderBy) {
        query = query.order(options.orderBy.column, {
          ascending: options.orderBy.ascending ?? true,
        });
      }

      // Apply pagination
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching members:', error);
      return { data: null, error };
    }
  },

  /**
   * Fetch financial health data with optional filtering
   *
   * ⚠️ RANK 44 — DIRECT SUPABASE RAW READ (BUG-SCORING-PATH-DIVERGENCE-01)
   * Queries financial_health_stats_v2 directly without official publication
   * contract enforcement. Full remediation requires a SQL view join.
   */
  async getFinancialHealthData(options?: {
    memberId?: string;
    month?: string;
    limit?: number;
    orderBy?: { column: string; ascending?: boolean };
    dataSource?: DataSource;
  }) {
    try {
      const tableName = this.getFinancialStatsTable(options?.dataSource);
      let query = supabase.from(tableName).select('*');

      // Filter by member ID if provided
      if (options?.memberId) {
        query = query.eq('uuid', options.memberId);
      }

      // Filter by month if provided
      if (options?.month) {
        query = query.eq('month', options.month);
      }

      // Apply ordering
      if (options?.orderBy) {
        query = query.order(options.orderBy.column, {
          ascending: options.orderBy.ascending ?? true,
        });
      }

      // Apply limit
      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching financial health data:', error);
      return { data: null, error };
    }
  },

  /**
   * Fetch a single member by ID
   */
  async getMemberById(memberId: string) {
    try {
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .eq('uuid', memberId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      if (import.meta.env.DEV) console.error(`Error fetching member with ID ${memberId}:`, error);
      return { data: null, error };
    }
  },

  /**
   * Fetch financial trends for a member over time
   *
   * ⚠️ RANK 44 — DIRECT SUPABASE RAW READ (BUG-SCORING-PATH-DIVERGENCE-01)
   * Queries financial_health_stats_v2 directly without official publication
   * contract enforcement. Full remediation requires a SQL view join.
   */
  async getMemberFinancialTrends(
    memberId: string,
    options?: {
      months?: number;
      metrics?: string[];
      dataSource?: DataSource;
    }
  ) {
    try {
      const tableName = this.getFinancialStatsTable(options?.dataSource);
      let query = supabase
        .from(tableName)
        .select('*')
        .eq('uuid', memberId)
        .order('month', { ascending: true });

      // Limit to recent months if specified
      if (options?.months) {
        query = query.limit(options.months);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data if specific metrics are requested
      if (options?.metrics && data) {
        return {
          data: data.map(item => {
            const filteredItem: Record<string, any> = { month: item.month };
            options.metrics?.forEach(metric => {
              if (metric in item) {
                filteredItem[metric] = item[metric as keyof typeof item];
              }
            });
            return filteredItem;
          }),
          error: null,
        };
      }

      return { data, error: null };
    } catch (error) {
      if (import.meta.env.DEV) console.error(`Error fetching financial trends for member ${memberId}:`, error);
      return { data: null, error };
    }
  },

  /**
   * Fetch aggregated statistics across all members
   *
   * ⚠️ RANK 44 — DIRECT SUPABASE RAW READ (BUG-SCORING-PATH-DIVERGENCE-01)
   * Queries financial_health_stats_v2 directly without official publication
   * contract enforcement. Full remediation requires a SQL view join.
   */
  async getAggregateStats(options?: { month?: string; dataSource?: DataSource }) {
    try {
      const tableName = this.getFinancialStatsTable(options?.dataSource);
      let query = supabase.from(tableName).select('*');

      if (options?.month) {
        query = query.eq('month', options.month);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) {
        // Return zeros when no data available (no mock data)
        if (import.meta.env.DEV) console.log('📊 getAggregateStats: No data found, returning zeros');
        return {
          data: {
            averageVeraScore: 0,
            averageDtiRatio: 0,
            averageCashRatio: 0,
            totalGrossIncome: 0,
            memberCount: 0,
            month: options?.month || 'current',
          },
          error: null
        };
      }

      // Calculate aggregates from real data
      const aggregates = {
        averageVeraScore: calculateAverage(data, 'verascore'),
        averageDtiRatio: calculateAverage(data, 'dti_ratio'),
        averageCashRatio: calculateAverage(data, 'cash_lr_ratio'),
        totalGrossIncome: calculateSum(data, 'gross_income'),
        memberCount: data.length,
        month: options?.month || 'all',
      };

      return { data: aggregates, error: null };
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error calculating aggregate statistics:', error);
      return { data: null, error };
    }
  },
};

// Helper functions for calculations
function calculateAverage(data: any[], field: string): number {
  const validValues = data
    .map(item => item[field])
    .filter((value): value is number => typeof value === 'number' && !isNaN(value));
  return validValues.length > 0
    ? validValues.reduce((sum, value) => sum + value, 0) / validValues.length
    : 0;
}

function calculateSum(data: any[], field: string): number {
  return data
    .map(item => item[field])
    .filter((value): value is number => typeof value === 'number' && !isNaN(value))
    .reduce((sum, value) => sum + value, 0);
}

export default supabaseService;
